/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint-disable-next-line @typescript-eslint/no-var-requires
const mapper = require('object-mapper');

const getDataRoot = (r: any) => (r?.data ? r.data : r);

const dedupeByUrl = (arr: Array<{ url: string }>) => {
  const seen = new Set<string>();
  return arr.filter(x => (x?.url && !seen.has(x.url) ? (seen.add(x.url), true) : false));
};

const getMediaImages = (root: any) => {
  const mediaRaw = getDataRoot(root)?.marketing?.promotions?.customerPromotionById?.media;
  const media = Array.isArray(mediaRaw) ? mediaRaw : mediaRaw ? [mediaRaw] : [];

  // Prefer MBOP: Square -> Feature-Horizontal, then dedupe
  const mbop = media
    .flatMap(m => (Array.isArray(m?.loyaltyOffers) ? m.loyaltyOffers : m?.loyaltyOffers ? [m.loyaltyOffers] : []))
    .flatMap(lo => (Array.isArray(lo?.imageData) ? lo.imageData : lo?.imageData ? [lo.imageData] : []))
    .flatMap(d => {
      const alt = d?.alternateDescription ?? '';
      const s = d?.imageUrls?.square,
        f = d?.imageUrls?.featureHorizontal;
      const out: any[] = [];
      if (s) out.push({ altImageUrl: null, alternateText: alt, url: String(s) });
      if (f) out.push({ altImageUrl: null, alternateText: alt, url: String(f) });
      return out;
    });
  const mbopDeduped = dedupeByUrl(mbop);
  if (mbopDeduped.length) return mbopDeduped;

  // CTS fallback
  const cts = media
    .flatMap(m => (Array.isArray(m?.images) ? m.images : []))
    .filter((img: any) => !!img?.url)
    .map((img: any) => ({
      altImageUrl: img?.altImageUrl ?? null,
      alternateText: img?.alternateText ?? '',
      url: String(img?.url),
    }));
  return dedupeByUrl(cts);
};

const buildOfferDescIndexWithOrder = (root: any) => {
  const list = getDataRoot(root)?.marketing?.promotions?.customerPromotionById?.offersDescriptions ?? [];
  const order: string[] = [];
  const index: Record<string, Array<{ key: string; value: string }>> = {};
  list.forEach((rec: any) => {
    const id = String(rec?.id || '').split('.')[0]; // e.g., "1807A.descriptions" -> "1807A"
    if (!id) return;
    order.push(id);
    index[id] = (rec?.descriptions ?? []).map((d: any) => ({ key: d?.key, value: d?.value }));
  });
  return { index, order };
};

const mapOffers = (promoOffers: any[] = [], offerIdsInOrder: string[] = [], offerDescIndex: Record<string, any[]>) => {
  const len = Math.max(promoOffers.length, offerIdsInOrder.length);
  const out = [];
  for (let i = 0; i < len; i++) {
    const src = promoOffers[i] || {};
    const offerId = offerIdsInOrder[i];
    out.push({
      hurdle: {
        endDate: src?.endDate,
        hurdleType: {
          code: src?.hurdleType?.code,
          description: src?.hurdleType?.description,
          __typename: 'Lookup',
        },
        maximumHurdle: src?.maximumHurdle,
        startDate: src?.startDate,
        __typename: 'LoyaltyPromotionHurdle',
      },
      descriptions: {
        name: src?.name || '',
        descriptions: offerId && offerDescIndex[offerId] ? offerDescIndex[offerId] : [],
        __typename: 'PromotionOfferDescriptions',
      },
      id: offerId,
      __typename: 'LoyaltyPromotionOffer',
    });
  }
  return out;
};

const toLegacyCustomerPromotionDetail = (customerPromotionById: any, root: any) => {
  if (!customerPromotionById || typeof customerPromotionById !== 'object') return null;

  const node = getDataRoot(root)?.marketing?.promotions?.customerPromotionById;
  const promo = node?.promotions?.[0] ?? {};

  // IDs / Names
  const simpleId = promo?.id || (typeof node?.id === 'string' && node.id.split('.').pop()) || null;

  const headlineName = node?.descriptions?.[0]?.name ?? null;
  const uiName = promo?.name ?? null;

  const state = promo?.state
    ? { code: promo.state.code, description: promo.state.description, __typename: 'Lookup' }
    : undefined;

  const hurdleAchieved = typeof promo?.hurdleAchieved === 'number' ? promo.hurdleAchieved : 0;
  const hurdleBalance = typeof promo?.hurdleBalance === 'number' ? promo.hurdleBalance : 0;
  const earningLimited = !!promo?.earningLimited;

  const earnings = Array.isArray(promo?.earnings)
    ? promo.earnings.map((e: any) => ({
        quantity: e?.quantity,
        description: e?.description,
        type: { code: undefined, description: undefined, __typename: 'Lookup' },
      }))
    : [];

  // Offers
  const promoOffers = Array.isArray(promo?.offers) ? promo.offers : [];
  const { index: offerDescIndex, order: offerIdsInOrder } = buildOfferDescIndexWithOrder(root);
  const offers = mapOffers(promoOffers, offerIdsInOrder, offerDescIndex);

  // Media (MBOP-first)
  const images = getMediaImages(root).map((img: any) => ({
    altImageUrl: img?.altImageUrl,
    url: img?.url,
    alternateText: img?.alternateText,
    __typename: 'PromotionMediaImage',
  }));

  return {
    descriptions: { name: headlineName, descriptions: null, __typename: 'PromotionDescriptions' },
    earningLimited,
    earnings,
    offers,
    __typename: 'LoyaltyPromotion',
    hurdleAchieved,
    hurdleBalance,
    id: simpleId,
    state,
    name: uiName,
    media: { images, __typename: 'PromotionMedia' },
  };
};

const customerPromotionByIdTemplate = {
  'marketing?.promotions?.customerPromotionById': {
    key: 'data?.loyaltyPromotion',
    transform: (customerPromotionById: any, root: any) => toLegacyCustomerPromotionDetail(customerPromotionById, root),
  },
};

export const mapCustomerPromotionByIdToLegacy = (responseOrData: any) =>
  mapper(getDataRoot(responseOrData), customerPromotionByIdTemplate);
